import { useEffect, useMemo, useRef, useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { EditableSlotGroup, RichTextContent, useSlotGroup } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useCtaSheenStyles } from '../hooks/useCtaSheenStyles';
import { parseBackgroundVideo } from '../utils/heroVideoUtils';
import {
  HeroActionRail,
  railScheduleDef,
  railResidentDef,
  railApplyDef,
  railTourDef,
  railSelfGuidedDef,
} from './HeroActionRail';

const SLIDE_INTERVAL_MS = 6000;

const featuredImageDef: SlotGroupDefinition = {
  label: 'Background Media',
  fields: {
    video_url: {
      label: 'Background Video URL',
      type: 'url',
      defaultField: 'custom_property_featured_video',
      description: 'Optional. Vimeo, YouTube, or direct MP4/WebM. When set, replaces the image slider.',
    },
    image: {
      label: 'Fallback / Poster Image',
      type: 'image',
      defaultField: 'property_featured_image',
      description: 'Used when no slide images are set, and as the video poster.',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Property hero image',
    },
  },
};

/** Full-bleed hero slides from https://www.residencesatprairiefire.com/ */
const slide1Def: SlotGroupDefinition = {
  label: 'Slide 1',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        'https://resource.rentcafe.com/image/upload/q_auto,f_auto,w_1920/s3/2/5458/kcspaces-prairie-fire-10.jpg',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Clubhouse lounge at Residences at Prairiefire',
    },
  },
};

const slide2Def: SlotGroupDefinition = {
  label: 'Slide 2',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        'https://resource.rentcafe.com/image/upload/q_auto,f_auto,w_1920/s3/3/2285777/kcspaces-prairie-fire-20.jpg',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Community entrance at Residences at Prairiefire',
    },
  },
};

const slide3Def: SlotGroupDefinition = {
  label: 'Slide 3',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        'https://resource.rentcafe.com/image/upload/q_auto,f_auto,w_1920/s3/3/2285777/kcspaces-prairie-fire-19.jpg',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Leasing office entrance at Residences at Prairiefire',
    },
  },
};

const slide4Def: SlotGroupDefinition = {
  label: 'Slide 4',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        'https://resource.rentcafe.com/image/upload/q_auto,f_auto,w_1920/s3/2/5458/prairie%20fire%20-%2024.jpg',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Living room at Residences at Prairiefire',
    },
  },
};

const ctaLinkDef: SlotGroupDefinition = {
  label: 'CTA Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/floor-plans',
    },
    label: {
      label: 'Link Text',
      type: 'text',
      defaultValue: 'View Availability',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_self',
    },
  },
};

interface HeroSlide {
  src: string;
  alt: string;
}

export function Hero() {
  const bgImage = useSlotGroup('hero.bg_image', featuredImageDef);
  const slide1 = useSlotGroup('hero.slide_1', slide1Def);
  const slide2 = useSlotGroup('hero.slide_2', slide2Def);
  const slide3 = useSlotGroup('hero.slide_3', slide3Def);
  const slide4 = useSlotGroup('hero.slide_4', slide4Def);
  const ctaLink = useSlotGroup('hero.cta_link', ctaLinkDef);
  useCtaSheenStyles();

  const sectionRef = useRef<HTMLElement>(null);
  const imgWrapRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoReady, setVideoReady] = useState(false);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [current, setCurrent] = useState(0);

  const videoInfo = useMemo(
    () => parseBackgroundVideo(bgImage.video_url),
    [bgImage.video_url],
  );
  const showVideoBackground = !!videoInfo && !prefersReducedMotion;

  const slides = useMemo<HeroSlide[]>(() => {
    const fromSlots = [slide1, slide2, slide3, slide4]
      .map((slot) => ({
        src: (slot.image || '').trim(),
        alt: slot.alt || bgImage.alt || 'Property hero image',
      }))
      .filter((slide) => Boolean(slide.src));

    if (fromSlots.length > 0) return fromSlots;

    if (bgImage.image) {
      return [{ src: bgImage.image, alt: bgImage.alt || 'Property hero image' }];
    }

    return [];
  }, [slide1, slide2, slide3, slide4, bgImage.image, bgImage.alt]);

  const slideCount = slides.length;

  useEffect(() => {
    setPrefersReducedMotion(window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }, []);

  useEffect(() => {
    setVideoReady(false);
    setIframeLoaded(false);
  }, [videoInfo?.src, videoInfo?.type]);

  useEffect(() => {
    setCurrent(0);
  }, [slideCount]);

  useEffect(() => {
    if (!showVideoBackground || videoInfo?.type !== 'direct') return;
    const video = videoRef.current;
    if (!video) return;

    const onCanPlay = () => setVideoReady(true);
    video.addEventListener('canplay', onCanPlay);
    if (video.readyState >= 3) setVideoReady(true);

    return () => video.removeEventListener('canplay', onCanPlay);
  }, [showVideoBackground, videoInfo]);

  useEffect(() => {
    if (document.querySelector('link[data-vibe-font="raleway"]')) return;
    const preconnect1 = document.createElement('link');
    preconnect1.rel = 'preconnect';
    preconnect1.href = 'https://fonts.googleapis.com';
    document.head.appendChild(preconnect1);
    const preconnect2 = document.createElement('link');
    preconnect2.rel = 'preconnect';
    preconnect2.href = 'https://fonts.gstatic.com';
    preconnect2.crossOrigin = 'anonymous';
    document.head.appendChild(preconnect2);
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Raleway:ital,wght@0,400;0,700;1,400;1,700&display=swap';
    link.setAttribute('data-vibe-font', 'raleway');
    document.head.appendChild(link);
  }, []);

  // Parallax on the image stack (same as the original Vantage hero).
  useEffect(() => {
    if (showVideoBackground) return;
    const section = sectionRef.current;
    const imgWrap = imgWrapRef.current;
    if (!section || !imgWrap) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const onScroll = () => {
      const rect = section.getBoundingClientRect();
      const scrolled = -rect.top;
      if (scrolled < -window.innerHeight || scrolled > section.offsetHeight) return;
      imgWrap.style.transform = `translateY(${scrolled * 0.3}px)`;
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [showVideoBackground]);

  useEffect(() => {
    if (showVideoBackground || prefersReducedMotion || slideCount <= 1) return;
    const id = setInterval(() => {
      setCurrent((prev) => (prev + 1) % slideCount);
    }, SLIDE_INTERVAL_MS);
    return () => clearInterval(id);
  }, [showVideoBackground, prefersReducedMotion, slideCount]);

  return (
    <section ref={sectionRef} className="hero-section hero-rail-anchor relative w-full overflow-hidden">
      {showVideoBackground && videoInfo ? (
        <EditableSlotGroup groupPath="hero.bg_image" definition={featuredImageDef}>
          <div className="hero-bg-media absolute inset-0 bg-[var(--vibe-color-text)]">
            {bgImage.image && (
              <VibesitesImage
                url={bgImage.image}
                alt={bgImage.alt || ''}
                priority
                sizes="100vw"
                className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-1000 ${
                  videoInfo.type === 'embed'
                    ? iframeLoaded
                      ? 'opacity-0'
                      : 'opacity-100'
                    : videoReady
                      ? 'opacity-0'
                      : 'opacity-100'
                }`}
              />
            )}
            {videoInfo.type === 'embed' ? (
              <iframe
                src={videoInfo.src}
                className={`hero-video-iframe absolute inset-0 h-full w-full transition-opacity duration-1000 ${
                  iframeLoaded ? 'opacity-100' : 'opacity-0'
                }`}
                allow="autoplay; fullscreen"
                frameBorder="0"
                onLoad={() => setIframeLoaded(true)}
                title="Background video"
              />
            ) : (
              <video
                ref={videoRef}
                src={videoInfo.src}
                autoPlay
                muted
                loop
                playsInline
                className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-1000 ${
                  videoReady ? 'opacity-100' : 'opacity-0'
                }`}
              />
            )}
          </div>
        </EditableSlotGroup>
      ) : (
        /* Parallax + Ken Burns image slider — no visible controls */
        <div
          ref={imgWrapRef}
          className="hero-bg-media hero-slider absolute h-[140%]"
          style={{ top: '-20%', left: '-1%', right: '-1%', willChange: 'transform' }}
          aria-roledescription="carousel"
          aria-label="Property photos"
        >
          {slideCount > 0 ? (
            slides.map((slide, i) => (
              <div
                key={`${slide.src}-${i}`}
                className={`hero-slider-slide${i === current ? ' hero-slider-slide--active' : ''}`}
                aria-hidden={i !== current}
              >
                <VibesitesImage
                  url={slide.src}
                  alt={slide.alt}
                  priority={i === 0}
                  sizes="100vw"
                  className="h-full w-full object-cover"
                  style={{
                    animation: prefersReducedMotion
                      ? undefined
                      : 'heroKenBurns 30s ease-in-out infinite alternate',
                    willChange: prefersReducedMotion ? undefined : 'transform',
                    transformOrigin: 'bottom right',
                    imageRendering: 'high-quality' as any,
                  }}
                />
              </div>
            ))
          ) : (
            <div className="absolute inset-0 bg-[var(--vibe-color-text)]" />
          )}

          <EditableSlotGroup groupPath="hero.slide_1" definition={slide1Def} className="hidden" style={{ display: 'none' }}>
            <span />
          </EditableSlotGroup>
          <EditableSlotGroup groupPath="hero.slide_2" definition={slide2Def} className="hidden" style={{ display: 'none' }}>
            <span />
          </EditableSlotGroup>
          <EditableSlotGroup groupPath="hero.slide_3" definition={slide3Def} className="hidden" style={{ display: 'none' }}>
            <span />
          </EditableSlotGroup>
          <EditableSlotGroup groupPath="hero.slide_4" definition={slide4Def} className="hidden" style={{ display: 'none' }}>
            <span />
          </EditableSlotGroup>
          <EditableSlotGroup groupPath="hero.bg_image" definition={featuredImageDef} className="hidden" style={{ display: 'none' }}>
            <span />
          </EditableSlotGroup>
        </div>
      )}

      {/* When video is showing, keep slide wrappers mounted for schema 1:1. */}
      {showVideoBackground && (
        <>
          <EditableSlotGroup groupPath="hero.slide_1" definition={slide1Def} className="hidden" style={{ display: 'none' }}>
            <span />
          </EditableSlotGroup>
          <EditableSlotGroup groupPath="hero.slide_2" definition={slide2Def} className="hidden" style={{ display: 'none' }}>
            <span />
          </EditableSlotGroup>
          <EditableSlotGroup groupPath="hero.slide_3" definition={slide3Def} className="hidden" style={{ display: 'none' }}>
            <span />
          </EditableSlotGroup>
          <EditableSlotGroup groupPath="hero.slide_4" definition={slide4Def} className="hidden" style={{ display: 'none' }}>
            <span />
          </EditableSlotGroup>
        </>
      )}

      {/* Gradient overlay for text contrast */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/35 to-black/20" />

      {/* Content — left-aligned stack; extra right padding on lg+ reserves
          room for the action rail pinned to the right edge. */}
      <div className="hero-content relative z-10 flex h-full flex-col items-start justify-center px-6 sm:px-8 text-left text-white">
        <RichTextContent
          path="hero.subtitle"
          label="Subtitle"
          defaultValue="<p>In The Edge District</p>"
          as="h2"
          className="hero-entrance hero-entrance-1 hero-subtitle"
        />

        <RichTextContent
          path="hero.title"
          label="Title"
          defaultField="property_name"
          defaultValue="<p>Property Name</p>"
          as="h1"
          className="hero-entrance hero-entrance-2 hero-title"
        />

        <EditableSlotGroup groupPath="hero.cta_link" definition={ctaLinkDef}>
          <a
            href={ctaLink.url || '/floor-plans'}
            target={ctaLink._target || '_self'}
            className="hero-entrance hero-entrance-3 hero-cta vantage-cta-sheen group/cta inline-flex items-center justify-center"
          >
            <span className="relative z-10">{ctaLink.label || 'View Availability'}</span>
          </a>
        </EditableSlotGroup>
      </div>

      <HeroActionRail />

      {/* Schema-lint anchors — the interactive rail wrappers live in
          <HeroActionRail>; these hidden anchors keep the same-file wrapper
          check satisfied for the rail_* slot groups declared below. */}
      <EditableSlotGroup groupPath="hero.rail_schedule" definition={railScheduleDef} className="hidden" style={{ display: 'none' }}>
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup groupPath="hero.rail_resident" definition={railResidentDef} className="hidden" style={{ display: 'none' }}>
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup groupPath="hero.rail_apply" definition={railApplyDef} className="hidden" style={{ display: 'none' }}>
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup groupPath="hero.rail_tour" definition={railTourDef} className="hidden" style={{ display: 'none' }}>
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup groupPath="hero.rail_self_guided" definition={railSelfGuidedDef} className="hidden" style={{ display: 'none' }}>
        <span />
      </EditableSlotGroup>

      <style>{`
        @keyframes heroKenBurns {
          0% { transform: scale(1.01) translate(0, 0); }
          100% { transform: scale(1.05) translate(-0.8%, -0.5%); }
        }
        @keyframes heroEntrance {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .hero-entrance {
          animation: heroEntrance 0.8s cubic-bezier(0.22, 1, 0.36, 1) both;
        }
        .hero-entrance-1 { animation-delay: 0.15s; }
        .hero-entrance-2 { animation-delay: 0.35s; }
        .hero-entrance-3 { animation-delay: 0.55s; }

        /* Full viewport height — svh/dvh for mobile browser chrome. */
        .hero-section {
          /* Contain the hero-content (z-10) and action rail (z-20) in their own
             stacking context so the floating rail buttons don't out-stack
             fixed-position third-party widgets (e.g. chatbots) at the page root. */
          isolation: isolate;
          --hero-rail-slot-count: 5;
          --hero-rail-width: max(
            168px,
            min(280px, calc(100vh / 5))
          );
          --hero-rail-hamburger-height: var(--hero-rail-width);
          height: 100vh;
          height: 100svh;
          height: 100dvh;
          min-height: 100vh;
          min-height: 100svh;
          min-height: 100dvh;
        }
        @supports (height: 100dvh) {
          .hero-section {
            --hero-rail-width: max(
              168px,
              min(280px, calc(100dvh / 5))
            );
          }
        }

        /* Full-bleed crossfade slider — no arrows / dots. */
        .hero-slider-slide {
          position: absolute;
          inset: 0;
          opacity: 0;
          transition: opacity 1.1s ease-in-out;
          pointer-events: none;
        }
        .hero-slider-slide--active {
          opacity: 1;
        }
        @media (prefers-reduced-motion: reduce) {
          .hero-slider-slide { transition: none; }
        }

        /* Cover-fit Vimeo / YouTube iframe backgrounds. */
        .hero-video-iframe {
          border: 0;
          pointer-events: none;
          width: 100vw;
          height: 56.25vw;
          min-height: 100%;
          min-width: 177.78vh;
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
        }

        /* Hero copy — matches the reference Vantage home hero (Raleway,
           95px title / 20px subtitle / 16px CTA on desktop). */
        .hero-content {
          padding-left: 1.5rem;
        }
        @media (min-width: 768px) {
          .hero-content {
            padding-left: 4.4rem;
          }
        }
        @media (min-width: 1024px) {
          .hero-content {
            padding-right: var(--hero-rail-width);
          }
        }
        .hero-title {
          font-family: "Raleway", sans-serif;
          font-size: 50px;
          font-weight: 700;
          line-height: 1;
          letter-spacing: 0;
          text-transform: uppercase;
          color: #fff;
          text-shadow: 0 0 12px rgba(255, 255, 255, 0.15), 0 0 24px rgba(255, 255, 255, 0.06);
          margin: 0;
          max-width: 14ch;
        }
        .hero-title p,
        .hero-title span {
          font: inherit;
          color: inherit;
          text-transform: inherit;
          text-shadow: inherit;
          margin: 0;
        }
        /* Tablet: keep title below the fixed header; full 95px from lg up. */
        @media (min-width: 768px) {
          .hero-title { font-size: 64px; }
        }
        @media (min-width: 1024px) {
          .hero-title { font-size: 80px; }
        }
        .hero-subtitle {
          font-family: "Raleway", sans-serif;
          font-size: 20px;
          font-weight: 700;
          line-height: 1.2;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          color: #fff;
          text-shadow: 0 0 12px rgba(255, 255, 255, 0.15), 0 0 24px rgba(255, 255, 255, 0.06);
          margin: 0 0 0.35em;
          max-width: min(48ch, 100%);
        }
        .hero-subtitle p,
        .hero-subtitle span {
          font: inherit;
          color: inherit;
          text-transform: inherit;
          text-shadow: inherit;
          letter-spacing: inherit;
          margin: 0;
        }
        .hero-cta {
          font-family: "Raleway", sans-serif;
          font-size: 16px;
          font-weight: 700;
          line-height: 1.2;
          letter-spacing: 0;
          text-transform: uppercase;
          min-height: 51px;
          min-width: 25px;
          max-width: 258px;
          width: 100%;
          padding: 0 1.5rem;
          margin-top: 30px;
        }
        @media (min-width: 480px) {
          .hero-cta { width: auto; }
        }
        @media (prefers-reduced-motion: reduce) {
          .hero-entrance { animation: none; opacity: 1; transform: none; }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Hero',
  displayName: 'Hero Section',
  tokens: {
    'hero.title': {
      label: 'Title',
      type: 'richtext',
      defaultField: 'property_name',
      defaultValue: '<p>Property Name</p>',
    },
    'hero.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue: '<p>In The Edge District</p>',
    },
  },
  slotGroups: {
    'hero.bg_image': featuredImageDef,
    'hero.slide_1': slide1Def,
    'hero.slide_2': slide2Def,
    'hero.slide_3': slide3Def,
    'hero.slide_4': slide4Def,
    'hero.cta_link': ctaLinkDef,
    'hero.rail_schedule': {
      label: 'Rail · Schedule a Visit',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/schedule-a-tour' },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Schedule a Visit' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'hero.rail_resident': {
      label: 'Rail · Resident Access',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/resident-access' },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Resident Access' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'hero.rail_apply': {
      label: 'Rail · Apply Now',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/apply-now' },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Apply Now' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'hero.rail_tour': {
      label: 'Rail · Virtual Tour',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/virtual-tour' },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Virtual Tour' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'hero.rail_self_guided': {
      label: 'Rail · Self-Guided Tours',
      fields: {
        url: {
          label: 'URL',
          type: 'url',
          defaultField: 'custom_self_guided_tour_url',
          suggestedFields: ['custom_self_guided_tour_url'],
        },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Self-Guided Tours' },
        _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
      },
    },
  },
};

(Hero as any).contentSchema = contentSchema;

export default Hero;
